import React, { useContext, useEffect, useState } from 'react';
import { Typography, Box, Button, Chip, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../Helpers/Contexts/authContext';
import { AuthUser } from '../../../interfaces/Contexts';
import { getRegisterEvents } from '../../../Utils/apiService';
import routes from '../../../Utils/routes';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import moment from 'moment';

const EventsContent: React.FC = () => {
  const navigate = useNavigate();
  const {
    state: { user }
  } = useContext(AuthContext);
  const userData = user as AuthUser;
  const [createdEvents, setCreatedEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);

    getRegisterEvents(userData?.applicantId)
      .then((res) => {
        const data = JSON.parse(res.data);
        const createdByMeEvents = data?.filter((item: any) => item.createdbyMe === true) || [];
        setCreatedEvents(createdByMeEvents);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
      });
  }, [userData?.applicantId]);

  const handleHostEvent = () => {
    navigate('/events/host event');
  };

  const handleSetupFeedbackForm = (eventId: string) => {
    navigate(`/events/host event?eventId=${eventId}`);
  };

  const renderEventCard = (event: any) => {
    const isRejected = event.isRejected;
    const isApproved = event.isApproved;
    const isUpcoming = event.isUpcoming;
    const isInReview = !isRejected && !isApproved && !isUpcoming;

    return (
      <Box
        key={event.Id}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 2,
          borderRadius: '8px',
          backgroundColor: '#f9f9f9',
          border: '1px solid #E6E6E8',
          position: 'relative'
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} sx={{ textAlign: 'left' }}>
            <Typography variant="h6" sx={{ fontSize: { xs: '16px', sm: '18px' }, fontWeight: 600, mb: 1 }}>
              {event.eventTitle}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '12px', sm: '14px' }, color: '#666' }}>
              Event type: {event.eventType || 'Free'} | {event.sessionType || 'One-day'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ textAlign: 'left', mt: { xs: 2, sm: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AccessTimeIcon sx={{ fontSize: { xs: '14px', sm: '16px' }, color: '#666' }} />
              <Typography variant="body2" sx={{ fontSize: { xs: '12px', sm: '14px' }, color: '#666' }}>
                {moment(event.startDate).format('DD MMM YY, h:mm a')} - {moment(event.endDate).format('h:mm a')} IST
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocationOnIcon sx={{ fontSize: { xs: '14px', sm: '16px' }, color: '#666' }} />
              <Typography variant="body2" sx={{ fontSize: { xs: '12px', sm: '14px' }, color: '#666' }}>
                {event.location || 'Bangalore, India'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon sx={{ fontSize: { xs: '14px', sm: '16px' }, color: '#666' }} />
              <Typography variant="body2" sx={{ fontSize: { xs: '12px', sm: '14px' }, color: '#666' }}>
                {event.maxParticipants ?
                  `Maximum number of participants: ${event.maxParticipants}` :
                  `Total participants: ${event.totalParticipants || 50}`
                }
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: { xs: 'flex-start', sm: 'flex-start' },
            mt: { xs: 2, sm: 0 }
          }}>
            {(isApproved || isUpcoming) && (
              <Button
                variant="contained"
                size="small"
                onClick={() => handleSetupFeedbackForm(event.Id)}
                sx={{
                  backgroundColor: 'none',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#C5CAE9' },
                  textTransform: 'none',
                  borderRadius: '4px',
                  fontSize: { xs: '12px', sm: '13px' },
                  fontWeight: 500,
                  px: 2
                }}
              >
                Setup feedback form
              </Button>
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ textAlign: 'left', display: { xs: 'none', md: 'block' } }}>
          </Grid>
        </Grid>

        {isRejected && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{
              backgroundColor: '#FFF4F4',
              borderRadius: '4px',
              p: { xs: 1.5, sm: 2 },
              position: 'relative',
              textAlign: 'left'
            }}>
              <Typography variant="subtitle2" sx={{ color: '#F44336', fontWeight: 600, mb: 1, fontSize: { xs: '13px', sm: '14px' } }}>
                Venue not available
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '12px', sm: '14px' } }}>
                {event.rejectionReason || 'The requested venue is not available on the selected dates'}
              </Typography>
            </Box>
          </Box>
        )}

        {isInReview && (
          <Chip
            label="In review"
            size="small"
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              backgroundColor: '#FF9800',
              color: 'white',
              fontSize: { xs: '10px', sm: '12px' },
              height: { xs: '20px', sm: '24px' },
              borderRadius: '0 0 0 8px',
              paddingX: 1.5,
              boxShadow: 'none',
              border: 'none',
              '& .MuiChip-label': {
                padding: '0 8px'
              }
            }}
          />
        )}
        {isUpcoming && (
          <Chip
            label="Upcoming"
            size="small"
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              backgroundColor: '#2196F3',
              color: 'white',
              fontSize: { xs: '10px', sm: '12px' },
              height: { xs: '20px', sm: '24px' },
              borderRadius: '0 0 0 8px',
              paddingX: 1.5,
              boxShadow: 'none',
              border: 'none',
              '& .MuiChip-label': {
                padding: '0 8px'
              }
            }}
          />
        )}
        {isApproved && (
          <Chip
            label="Approved"
            size="small"
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              backgroundColor: '#4CAF50',
              color: 'white',
              fontSize: { xs: '10px', sm: '12px' },
              height: { xs: '20px', sm: '24px' },
              borderRadius: '0 0 0 8px',
              paddingX: 1.5,
              boxShadow: 'none',
              border: 'none',
              '& .MuiChip-label': {
                padding: '0 8px'
              },
              zIndex: 10
            }}
          />
        )}
        {isRejected && (
          <Chip
            label="Reason for rejection"
            size="small"
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              backgroundColor: '#F44336',
              color: 'white',
              fontSize: { xs: '10px', sm: '12px' },
              height: { xs: '20px', sm: '24px' },
              borderRadius: '0 0 0 8px',
              paddingX: 1.5,
              boxShadow: 'none',
              border: 'none',
              '& .MuiChip-label': {
                padding: '0 8px'
              }
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3, backgroundColor: 'white', borderRadius: '8px' }}>
      <Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography>Loading...</Typography>
          </Box>
        ) : createdEvents.length === 0 ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: 4,
            textAlign: 'center'
          }}>
            <EventIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
            <Typography variant="body1" sx={{ mb: 2, color: '#666' }}>
              You haven't created any events yet
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleHostEvent}
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              Host Your First Event
            </Button>
          </Box>
        ) : (
          <>
            {createdEvents.map((event) => renderEventCard(event))}
          </>
        )}
      </Box>
    </Box>
  );
};

export default EventsContent;
